import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import {
  ItemDescriptionFragment,
  MissionLabel,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame } from '@/core/architecture'
import { individualHiddenItemInfo } from '@/core/individual'
import { encodeSkuId } from '@/core/sku'
import { filterHiddenItem } from '@/lib/array'

type SupplementLinkSetCell = {
  artistId: string
  itemId?: string
  commentCount?: number
  /** 関連するアイテムミッション */
  relatedMissions?: ItemDescriptionFragment['relatedMissions']
  /** 「情報」の表示有無 */
  hiddenItemInfo?: boolean
  /** アイテムがプロテクト状態か否か */
  isUserProtected?: boolean
  /** ドロワー「NFTアイテム」の表示有無 */
  issuableNft?: boolean
  isResaleAvailable?: boolean
  /* ユーザーがアイテムを出品しているかどうか */
  isResaled?: boolean
  /** SKUユニットの情報 */
  sku?: ItemDescriptionFragment['sku']
  /** 配送ボタンをクリックしたときの処理 */
  onClickMerch?: () => void
  /** 「全画面」をクリックしたときの挙動 */
  onClickExpansion?: () => void
  /** ドロワー「プロテクト」をクリックしたときの挙動 */
  onClickProtectUserItem?: () => void
}
/**
 * アイテム詳細ページ下部にあるボタン群のコンポーネント
 */
export const SupplementLinkSetCell: Cell<SupplementLinkSetCell> = ({
  itemId,
  commentCount,
  relatedMissions,
  artistId,
  hiddenItemInfo,
  isUserProtected,
  issuableNft,
  isResaleAvailable,
  isResaled,
  onClickMerch,
  onClickExpansion,
  onClickProtectUserItem,
  sku,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { routerPages, generatePath, generateQueryPath } = useRouter()
  const { analytics } = useAnalytics()
  const [open, setOpen] = useState<boolean>(false)

  const supplements = [
    {
      title: 'itemInfo',
      hidden: individualHiddenItemInfo(),
      icon: <Icons.ItemInfo sx={{ mx: 'auto', color: 'grey.600' }} />,
      href: `${generatePath(routerPages.OwnedItemInformation, { itemId })}${
        sku ? `?skuId=${encodeSkuId(sku?.id)}` : ''
      }`,
      color: 'text.secondary',
      clickable: true,
    },
    {
      title: 'expansion',
      icon: <Icons.Zoom sx={{ mx: 'auto', color: 'grey.600' }} />,
      onClick: onClickExpansion,
      color: 'text.secondary',
      clickable: true,
    },
    {
      title: 'merch',
      icon: <Icons.Delivery sx={{ mx: 'auto', color: 'grey.600' }} />,
      onClick: onClickMerch,
      color: 'text.secondary',
    },
    {
      title: 'mission',
      icon: (
        <Badge
          invisible={
            !relatedMissions?.filter(o => o.label === MissionLabel.New)?.length
          }
          sx={{
            '.MuiBadge-badge': {
              bgcolor: 'grey.200',
              padding: '2px 3px',
              height: 'auto',
              width: 'auto',
              minHeight: 15,
              minWidth: 15,
              top: 4,
              right: 4,
            },
          }}
        >
          <Icons.Mission sx={{ mx: 'auto', color: 'grey.600' }} />
        </Badge>
      ),
      href: generatePath(routerPages.ItemMissions, { artistId, itemId }),
      color: 'text.secondary',
      disabled: !relatedMissions?.length,
    },
  ]

  const drawerMenu = [
    {
      title: isUserProtected ? 'protectLabel' : 'unprotectLabel',
      icon: <Icons.Protect sx={{ mx: 'auto', color: 'grey.600' }} />,
      component: Box,
      onClick: onClickProtectUserItem
        ? async () => {
            await onClickProtectUserItem()
            setOpen(false)
          }
        : undefined,
      hidden: !config.modules.trade || !onClickProtectUserItem,
    },
    {
      title: isResaled ? 'resaleLabelResaleOn' : 'resaleLabel',
      icon: <Icons.Resale sx={{ mx: 'auto', color: 'grey.600' }} />,
      href: isResaled
        ? generatePath(routerPages.ResaleManagement)
        : generateQueryPath(
            routerPages.ResaleCreate,
            { itemId },
            { skuId: encodeSkuId(sku?.id ?? '') }
          ),
      hidden: !isResaleAvailable,
    },
    {
      title: 'nftLabel',
      icon: <Icons.Nft sx={{ mx: 'auto', color: 'grey.600' }} />,
      href: generatePath(routerPages.OwnedItemNftItems, { itemId }),
      /** NFT 停止に向けて、NFT 化ページへの動線を塞ぐ */
      hidden: true,
    },
  ]

  return (
    <CellFrame>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2, px: 4, minHeight: 64 }}
      >
        <Stack direction="row" justifyContent="flex-start">
          {!!filterHiddenItem(drawerMenu).length && (
            <IconButton.Menu
              color="grey.600"
              sx={{ p: 3 }}
              onClick={() => setOpen(true)}
            />
          )}
          {supplements.map(
            (
              { title, icon, onClick, href, color, disabled, hidden },
              index
            ) => {
              if (!onClickExpansion && title === 'expansion') return
              if (!onClickMerch && title === 'merch') return
              if (hiddenItemInfo && title === 'itemInfo') return

              if (hidden) return

              return (
                <Button
                  key={index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    p: 1,
                    color,
                    borderRadius: 0,
                    textAlign: 'center',
                    minWidth: 44,
                    '&.Mui-disabled': {
                      background: 'inherit',
                      color: 'text.disabled',
                      svg: {
                        color: 'text.disabled',
                      },
                    },
                  }}
                  onClick={onClick}
                  href={href}
                  variant="text"
                  disabled={disabled}
                >
                  {icon}
                  <Typography
                    variant="overline"
                    sx={{ pt: 1, color: 'inherit' }}
                  >
                    {t(title)}
                  </Typography>
                </Button>
              )
            }
          )}
        </Stack>
        <Button
          variant="text"
          sx={{
            p: 1,
            borderRadius: 0,
            minWidth: 44,
            alignSelf: 'stretch',
            display: 'flex',
            height: 'unset',
            color: 'text.secondary',
          }}
          onClick={() => {
            analytics?.track('click_comment', {
              applicationKey: config.applicationKey,
              artistId,
              name: 'owned_item',
            })
          }}
          href={generatePath(routerPages.ItemComment, { itemId })}
        >
          <Icons.Reply sx={{ color: 'grey.600' }} />
          <Typography variant="body2" sx={{ pl: 1, color: 'inherit' }}>
            {commentCount}
          </Typography>
        </Button>
      </Stack>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      >
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {t('select')}
          </Typography>
        </Box>
        <Divider />
        <List>
          {filterHiddenItem(drawerMenu).map(
            ({ title, icon, ...props }, index) => (
              <ListItemButton key={index} sx={{ height: 52 }} {...props}>
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText
                  primary={t(title)}
                  slotProps={{
                    primary: { variant: 'h6', sx: { pl: 3 } },
                  }}
                />
              </ListItemButton>
            )
          )}
          <Divider />
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={() => setOpen(false)}
          >
            <ListItemText
              primary={t('cancel')}
              slotProps={{
                primary: {
                  variant: 'h6',
                  align: 'center',
                  color: 'primary',
                },
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>
    </CellFrame>
  )
}
